"use client";

import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import { fetchData } from "@/utils/api-handler";
import { useProtectedQuery } from "@/hooks/use-protected-query";
import { useCommandProjects } from "@/hooks/use-command-projects";
import { useCommandSessions } from "@/hooks/use-command-sessions";
import type { CommandProject, CommandSession, CommandSessionList } from "@/types/command-agent";

interface UseCommandV2SearchOptions {
    agentId: string | null;
    search: string;
    enabled?: boolean;
}

export interface CommandV2SearchSession {
    id: string;
    title: string;
    updatedAt: string;
    projectId: string | null;
}

export interface CommandV2SearchProject {
    id: string;
    name: string;
}

function mapSession(session: CommandSession): CommandV2SearchSession {
    const createdAt = session.created_at ?? new Date().toISOString();
    return {
        id: session.id,
        title: session.title?.trim() || "Untitled chat",
        updatedAt: session.updated_at ?? createdAt,
        projectId: session.project_id ?? null,
    };
}

function mapProject(project: CommandProject): CommandV2SearchProject {
    return { id: project.id, name: project.name };
}

export function useCommandV2Search({
    agentId,
    search,
    enabled = true,
}: UseCommandV2SearchOptions) {
    const trimmed = search.trim();
    const hasQuery = trimmed.length > 0;

    const {
        allProjects: matchedProjects,
        isLoadingProjects,
        isFetchingProjects,
    } = useCommandProjects({ search: trimmed, enabled });

    const { allProjects: catalogProjects } = useCommandProjects({
        enabled: enabled && hasQuery,
    });

    const {
        allSessions: unfiledSessions,
        isLoadingSessions,
        isFetchingSessions,
    } = useCommandSessions(agentId, {
        search: trimmed,
        projectId: null,
        enabled,
    });

    const { hasPermission: canListSessions } = useProtectedQuery({
        queryKey: ["_gated_list_command_sessions"],
        queryFn: async () => null,
        requiredPermissions: ["LIST_COMMAND_SESSIONS"],
        enabled: false,
    });

    const projectIds = hasQuery ? catalogProjects.map((project) => project.id) : [];

    const projectSessionQueries = useQueries({
        queries: projectIds.map((projectId) => ({
            queryKey: ["command-project-sessions", projectId, trimmed],
            queryFn: async () => {
                const params = new URLSearchParams({
                    page: "1",
                    limit: "50",
                    sort_by: "updated_at",
                    sort_order: "desc",
                });
                if (trimmed) params.set("search", trimmed);
                return fetchData(
                    `/api/command-team/projects/${projectId}/sessions?${params.toString()}`
                ) as Promise<CommandSessionList>;
            },
            enabled: enabled && canListSessions && Boolean(projectId),
            staleTime: 30_000,
        })),
    });

    const sessions = useMemo(() => {
        const filed = projectSessionQueries.flatMap((query) => {
            const list = query.data as CommandSessionList | undefined;
            return list?.items ?? [];
        });
        const filedIds = new Set(filed.map((session) => session.id));
        const unfiled = unfiledSessions.filter(
            (session) => !session.project_id && !filedIds.has(session.id)
        );
        return [...unfiled, ...filed].map(mapSession);
    }, [unfiledSessions, projectSessionQueries]);

    const projects = useMemo(() => {
        const byId = new Map<string, CommandV2SearchProject>();
        matchedProjects.forEach((project) => byId.set(project.id, mapProject(project)));
        catalogProjects.forEach((project) => {
            if (!byId.has(project.id)) byId.set(project.id, mapProject(project));
        });
        const matchedIds = new Set(matchedProjects.map((project) => project.id));
        const neededIds = new Set(matchedIds);
        sessions.forEach((session) => {
            if (session.projectId) neededIds.add(session.projectId);
        });
        return [...neededIds]
            .map((id) => byId.get(id))
            .filter((project): project is CommandV2SearchProject => Boolean(project));
    }, [matchedProjects, catalogProjects, sessions]);

    const isLoadingProjectSessions = projectSessionQueries.some((query) => query.isLoading);
    const isFetchingProjectSessions = projectSessionQueries.some((query) => query.isFetching);

    return {
        projects,
        sessions,
        isLoading:
            isLoadingProjects ||
            isLoadingSessions ||
            isLoadingProjectSessions,
        isFetching:
            isFetchingProjects ||
            isFetchingSessions ||
            isFetchingProjectSessions,
    };
}
